// src/App.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Styles.css';
import Navbar from './Navbar';
import Footer from './Footer';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import ManagementPage from "./ManagementPage";
import ExistingOrdersPage from './ExistingOrdersPage';
import FeedbackPage from './FeedbackPage';
import { apiFetch, normalizeMenus, normalizeVillas } from './api';

const CATEGORIES = [
    { key: 'breakfast_packages', label: 'Breakfast Packages' },
    { key: 'breakfast', label: 'Breakfast' },
    { key: 'drinks', label: 'Drinks' },
    { key: 'fruits', label: 'Fruits' },
    { key: 'extras', label: 'Extras' }
];

function getQueryParam(param) {
    const params = new URLSearchParams(window.location.search);
    return params.get(param) || '';
}

function buildSearch(villaId, lastName, arrivalDate) {
    const params = new URLSearchParams();
    if (villaId) params.set('villa', villaId);
    if (lastName) params.set('lastName', lastName);
    if (arrivalDate) params.set('arrivalDate', arrivalDate);
    return params.toString();
}

function MenuItemImage({ src, alt, className }) {
    const FALLBACK_IMAGE = '/static/no-image.jpg';
    const [sourceIndex, setSourceIndex] = useState(0);

    const resolveImageSources = useCallback((path) => {
        if (!path) return [FALLBACK_IMAGE];
        const normalizedPath = String(path).trim();
        if (!normalizedPath) return [FALLBACK_IMAGE];

        if (normalizedPath.startsWith('/public/')) return [normalizedPath.replace('/public/', '/')];
        if (normalizedPath.startsWith('public/')) return [`/${normalizedPath.replace('public/', '')}`];
        if (normalizedPath.startsWith('static/')) return [`/${normalizedPath}`];
        if (normalizedPath.startsWith('data:')) return [normalizedPath];

        if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            try {
                const parsedUrl = new URL(normalizedPath);
                const isGoogleDrive = parsedUrl.hostname === 'drive.google.com' || parsedUrl.hostname.endsWith('.drive.google.com');
                if (isGoogleDrive) {
                    const idFromPath = parsedUrl.pathname.match(/\/file\/d\/([^/]+)/)?.[1];
                    const idFromQuery = parsedUrl.searchParams.get('id');
                    const fileId = idFromPath || idFromQuery;
                    if (fileId) {
                        const encodedId = encodeURIComponent(fileId);
                        return [
                            `https://lh3.googleusercontent.com/d/${encodedId}=w2000`,
                            `https://drive.google.com/thumbnail?id=${encodedId}&sz=w2000`,
                            `https://drive.google.com/uc?export=view&id=${encodedId}`,
                        ];
                    }
                }
            } catch {
                return [normalizedPath];
            }
            return [normalizedPath];
        }

        return [normalizedPath];
    }, []);

    const imageSources = useMemo(() => resolveImageSources(src), [resolveImageSources, src]);

    useEffect(() => {
        setSourceIndex(0);
    }, [src]);

    const hasNextSource = sourceIndex < imageSources.length - 1;
    const imageSrc = hasNextSource ? imageSources[sourceIndex] : (imageSources[sourceIndex] || FALLBACK_IMAGE);

    const handleImageError = () => {
        if (hasNextSource) {
            setSourceIndex((prev) => prev + 1);
            return;
        }
        if (imageSrc !== FALLBACK_IMAGE) {
            setSourceIndex(imageSources.length);
        }
    };

    const finalImageSrc = sourceIndex >= imageSources.length ? FALLBACK_IMAGE : imageSrc;

    return (
        <img
            src={finalImageSrc}
            alt={alt}
            className={className}
            referrerPolicy="no-referrer"
            onError={handleImageError}
        />
    );
}

function QuantityControl({ value, onChange }) {
    const handleDecrease = () => onChange(Math.max(0, value - 1));
    const handleIncrease = () => onChange(value + 1);

    return (
        <div className="quantity-control">
            <button className="quantity-btn" onClick={handleDecrease} aria-label="Decrease">
                <span className="quantity-icon">−</span>
            </button>
            <span className="quantity-value">{value}</span>
            <button className="quantity-btn" onClick={handleIncrease} aria-label="Increase">
                <span className="quantity-icon">+</span>
            </button>
        </div>
    );
}

const toMinutes = (t) => {
    if (!t) return null;
    const parts = t.split(':');
    const h = parseInt(parts[0] || '0', 10);
    const m = parseInt(parts[1] || '0', 10);
    return h * 60 + m;
};

// Modal for order confirmation.
function ConfirmationModal({
                              orderSummary,
                              selectedDate,
                              selectedTime,
                              submissionError,
                              isSubmitting,
                              handleOrderSubmit,
                              onClose,
                              searchString,
                              scrollToFirstCategory
                          }) {
     const navigate = useNavigate();

     const handleEditOrder = () => {
         onClose();
         // Delay navigation to allow modal to close
         setTimeout(() => {
             navigate(`/?${searchString}`);
             // Call scroll function after navigation
             setTimeout(() => {
                 scrollToFirstCategory?.();
             }, 100);
         }, 50);
     };

    if (!orderSummary) return null;

    return (
        <div className="confirmation-modal-overlay">
            <div className="confirmation-modal-content">
                <button className="confirmation-modal-close" onClick={onClose}>×</button>
                <h2>Confirm Your Order</h2>
                {selectedDate && <p><strong>Date:</strong> {selectedDate.toLocaleDateString('en-GB')}</p>}
                {selectedTime && <p><strong>Time:</strong> {selectedTime}</p>}
                <h3>Order Summary</h3>
                <div className="modal-order-summary">
                    <table className="orders-table">
                        <thead>
                        <tr>
                            <th>Quantity</th>
                            <th>Item</th>
                            <th>Price</th>
                        </tr>
                        </thead>
                        <tbody>
                        {orderSummary.order_items.map((item, index) => (
                            <tr key={index}>
                                <td>{item.quantity}</td>
                                <td>{item.name}</td>
                                <td>
                                    {item.price === 0 && item.discount > 0
                                        ? 'included'
                                        : item.price != null
                                            ? item.price.toLocaleString('en-GB') + ' IDR'
                                            : ''}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                        <tfoot>
                        <tr>
                            <td colSpan="2" className="total-price-label">Total Price:</td>
                            <td className="total-price-value">
                                {orderSummary.total_price != null
                                    ? `${orderSummary.total_price.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IDR`
                                    : '0 IDR'}
                            </td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
                {submissionError && <p className="menu-selection-error">{submissionError}</p>}
                <div className="confirmation-modal-actions">
                    <button
                        onClick={handleEditOrder}
                        className="selection-btn"
                    >
                        EDIT ORDER
                    </button>
                    <button
                        onClick={handleOrderSubmit}
                        disabled={isSubmitting}
                        className={`selection-btn ${isSubmitting ? 'menu-selection-btn-disabled' : ''}`}
                    >
                        {isSubmitting ? 'PLACING ORDER...' : 'PLACE YOUR ORDER'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function App() {
    const resolveBackgroundImageUrl = (rawValue, fallbackValue) => {
        const candidate = (rawValue || fallbackValue || '').trim();
        if (!candidate) return '';
        if (/^https?:\/\//i.test(candidate) || candidate.startsWith('data:')) return candidate;
        if (candidate.startsWith('/')) {
            const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
            return `${publicUrl}${candidate}`;
        }
        return candidate;
    };

    const backgroundImageUrl = resolveBackgroundImageUrl(
        window._env_?.REACT_APP_VILLA_BACKGROUND_IMAGE
        || window._env_?.REACT_APP_BACKGROUND_IMAGE
        || process.env.REACT_APP_BACKGROUND_IMAGE,
        '/static/food.jpg'
    );
    const orderConfirmationBackgroundImageUrl = resolveBackgroundImageUrl(
        window._env_?.REACT_APP_ORDER_CONFIRMATION_BACKGROUND_IMAGE
        || process.env.REACT_APP_ORDER_CONFIRMATION_BACKGROUND_IMAGE,
        '/static/villa2.jpg'
    );
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/orders/api';
    const weatherLat = window._env_?.REACT_APP_WEATHER_LAT || process.env.REACT_APP_WEATHER_LAT || '-8.65';
    const weatherLon = window._env_?.REACT_APP_WEATHER_LON || process.env.REACT_APP_WEATHER_LON || '115.22';
    const weatherPlace = window._env_?.REACT_APP_WEATHER_PLACE || process.env.REACT_APP_WEATHER_PLACE || 'Bali';
    const weatherTimezone = window._env_?.REACT_APP_WEATHER_TIMEZONE || process.env.REACT_APP_WEATHER_TIMEZONE || 'Asia/Makassar';

    const [navbarOpen, setNavbarOpen] = useState(false);
    const [baliNow, setBaliNow] = useState(null);
    const [weatherNow, setWeatherNow] = useState(null);
    const [nextFiveHoursWeather, setNextFiveHoursWeather] = useState([]);
    const [weatherError, setWeatherError] = useState(null);
    const [weatherLoading, setWeatherLoading] = useState(true);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const [navbarHeight, setNavbarHeight] = useState(0);
    const [liveConditionsVariant, setLiveConditionsVariant] = useState(() => {
        const savedVariant = window.localStorage.getItem('vmb_live_conditions_variant');
        return savedVariant === 'soft' || savedVariant === 'compact' ? savedVariant : 'compact';
    });
    const [temperatureUnit, setTemperatureUnit] = useState(() => {
        const savedUnit = window.localStorage.getItem('vmb_temperature_unit');
        return savedUnit === 'F' ? 'F' : 'C';
    });
    const isMobileCalendarLayout = windowWidth <= 800;

    // Calculate number of forecast hours based on window width (max 8 hours)
    const getHourCount = useCallback(() => {
        if (windowWidth >= 900) return 8;
        if (windowWidth >= 600) return 5;
        return 3;
    }, [windowWidth]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const updateNavbarHeight = () => {
            const navbarElement = document.querySelector('.navbar');
            setNavbarHeight(navbarElement ? navbarElement.offsetHeight : 0);
        };

        updateNavbarHeight();
        window.addEventListener('resize', updateNavbarHeight);

        return () => {
            window.removeEventListener('resize', updateNavbarHeight);
        };
    }, [navbarOpen]);

    const [isWelcomeInstructionsVisible, setIsWelcomeInstructionsVisible] = useState(false);
    const [isMenuInfoVisible, setIsMenuInfoVisible] = useState(false);
    const [isOrderConfirmationInfoVisible, setIsOrderConfirmationInfoVisible] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTime, setSelectedTime] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [error, setError] = useState(null);

    const [menus, setMenus] = useState([]);
    const [loadingMenus, setLoadingMenus] = useState(true);
    const [menuError, setMenuError] = useState(null);
    const [quantities, setQuantities] = useState({});
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [isOrderSubmitted, setIsOrderSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    const [lastName, setLastName] = useState(getQueryParam('lastName'));
    const [arrivalDate, setArrivalDate] = useState(getQueryParam('arrivalDate'));
    const [openCategory, setOpenCategory] = useState(null);
    const [email, setEmail] = useState('');

    const [villas, setVillas] = useState([]);
    const [loadingVillas, setLoadingVillas] = useState(true);
    const [villaError, setVillaError] = useState(null);
    const [selectedVillaId, setSelectedVillaId] = useState(getQueryParam('villa'));


    const [guestData, setGuestData] = useState(null);
    const [loadingGuestData, setLoadingGuestData] = useState(false);
    const [guestDataError, setGuestDataError] = useState(null);

    const [isGuestAuthorized, setIsGuestAuthorized] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [isAuthorizing, setIsAuthorizing] = useState(false);
    const [guestAuthToken, setGuestAuthToken] = useState('');
    const [autoAuthSuppressed, setAutoAuthSuppressed] = useState(false);

    const [orderSummary, setOrderSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const timeSelectionRef = useRef(null);
    const welcomeSectionRef = useRef(null);
    const calendarSectionRef = useRef(null);
    const menuSelectionRef = useRef(null);
    const orderSummarySectionRef = useRef(null);
    const menuInfoRef = useRef(null);
    const menuHeadingRef = useRef(null);
    const menuHeaderContainerRef = useRef(null);
    const menuCategoryNavigationRef = useRef(null);
    const confirmationInfoRef = useRef(null);
    const menuCategoryContainerRef = useRef(null);
    const wheelScrollLockRef = useRef(false);
    const isReloadScrollResetRef = useRef(false);

    const [fetchedOrder, setFetchedOrder] = useState(null);
    const [loadingFetchedOrder, setLoadingFetchedOrder] = useState(false);
    const [fetchOrderError, setFetchOrderError] = useState(null);
    const redirectReason = getQueryParam('reason');

    useEffect(() => {
        const navigationEntry = window.performance.getEntriesByType('navigation')[0];
        const isReloadNavigation = navigationEntry?.type === 'reload';

        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        if (!isReloadNavigation) return;

        isReloadScrollResetRef.current = true;
        window.sessionStorage.removeItem('vmb_scroll_to_menu_info');

        // Run twice to override late browser/UI restoration on hard reload.
        window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            window.setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                isReloadScrollResetRef.current = false;
            }, 120);
        });
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    const weatherCodeToLabel = (code) => {
        const mapping = {
            0: 'Clear sky',
            1: 'Mainly clear',
            2: 'Partly cloudy',
            3: 'Overcast',
            45: 'Fog',
            48: 'Depositing rime fog',
            51: 'Light drizzle',
            53: 'Moderate drizzle',
            55: 'Dense drizzle',
            56: 'Freezing drizzle',
            57: 'Dense freezing drizzle',
            61: 'Slight rain',
            63: 'Moderate rain',
            65: 'Heavy rain',
            66: 'Freezing rain',
            67: 'Heavy freezing rain',
            71: 'Slight snowfall',
            73: 'Moderate snowfall',
            75: 'Heavy snowfall',
            77: 'Snow grains',
            80: 'Rain showers',
            81: 'Rain showers',
            82: 'Violent rain showers',
            85: 'Snow showers',
            86: 'Heavy snow showers',
            95: 'Thunderstorm',
            96: 'Thunderstorm with hail',
            99: 'Thunderstorm with hail',
        };
        return mapping[code] || 'Unknown';
    };

    const weatherCodeToIcon = (code) => {
        if (code === 0) return '☀️';
        if ([1, 2].includes(code)) return '🌤️';
        if (code === 3) return '☁️';
        if ([45, 48].includes(code)) return '🌫️';
        if ([51, 53, 55, 56, 57].includes(code)) return '🌦️';
        if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return '🌧️';
        if ([71, 73, 75, 77, 85, 86].includes(code)) return '❄️';
        if ([95, 96, 99].includes(code)) return '⛈️';
        return '🌡️';
    };

    useEffect(() => {
        const fetchBaliNow = () => {
            apiFetch(`${baseUrl}/datetime/current`)
                .then(res => {
                    if (!res.ok) throw new Error('Failed to fetch Bali time.');
                    return res.json();
                })
                .then(data => setBaliNow(new Date(data)))
                .catch(() => setBaliNow(new Date()));
        };

        fetchBaliNow();
        const syncInterval = setInterval(fetchBaliNow, 15 * 60 * 1000);
        return () => clearInterval(syncInterval);
    }, [baseUrl]);

    useEffect(() => {
        if (!baliNow) return;
        const tick = setInterval(() => {
            setBaliNow(prev => (prev ? new Date(prev.getTime() + 1000) : null));
        }, 1000);
        return () => clearInterval(tick);
    }, [baliNow]);

    useEffect(() => {
        const fetchWeather = async () => {
            if (!selectedDate) {
                setWeatherNow(null);
                setNextFiveHoursWeather([]);
                setWeatherError(null);
                setWeatherLoading(false);
                return;
            }

            setWeatherError(null);
            setWeatherLoading(true);
            try {
                const selectedDateIso = [
                    selectedDate.getFullYear(),
                    String(selectedDate.getMonth() + 1).padStart(2, '0'),
                    String(selectedDate.getDate()).padStart(2, '0'),
                ].join('-');
                const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${encodeURIComponent(weatherLat)}&longitude=${encodeURIComponent(weatherLon)}&hourly=temperature_2m,weather_code,precipitation_probability&timezone=${encodeURIComponent(weatherTimezone)}&start_date=${selectedDateIso}&end_date=${selectedDateIso}`;
                const response = await apiFetch(weatherUrl);
                if (!response.ok) throw new Error('Failed to load weather data.');
                const data = await response.json();

                const hourly = data.hourly || {};
                const hourlyTimes = Array.isArray(hourly.time) ? hourly.time : [];
                const hourlyTemps = Array.isArray(hourly.temperature_2m) ? hourly.temperature_2m : [];
                const hourlyCodes = Array.isArray(hourly.weather_code) ? hourly.weather_code : [];
                const hourlyPrecip = Array.isArray(hourly.precipitation_probability) ? hourly.precipitation_probability : [];

                if (hourlyTimes.length === 0) {
                    throw new Error('Weather data is unavailable for the selected date.');
                }

                const firstMorningIndex = hourlyTimes.findIndex((time) => Number(String(time).slice(11, 13)) >= 7);
                const nextSliceStart = firstMorningIndex >= 0 ? firstMorningIndex : 0;
                const hourCount = getHourCount();

                const nextItems = hourlyTimes.slice(nextSliceStart, nextSliceStart + hourCount).map((time, idx) => ({
                    time,
                    temperature: hourlyTemps[nextSliceStart + idx],
                    weatherCode: hourlyCodes[nextSliceStart + idx],
                    precipitationProbability: hourlyPrecip[nextSliceStart + idx],
                    description: weatherCodeToLabel(hourlyCodes[nextSliceStart + idx]),
                    icon: weatherCodeToIcon(hourlyCodes[nextSliceStart + idx]),
                }));

                setNextFiveHoursWeather(nextItems);
                const headlineItem = nextItems[0] || null;
                if (headlineItem) {
                    setWeatherNow({
                        temperature: headlineItem.temperature,
                        weatherCode: headlineItem.weatherCode,
                        description: headlineItem.description,
                        icon: headlineItem.icon,
                    });
                } else {
                    setWeatherNow(null);
                }
            } catch (err) {
                setWeatherError(err.message || 'Weather data is currently unavailable.');
                setWeatherNow(null);
                setNextFiveHoursWeather([]);
            } finally {
                setWeatherLoading(false);
            }
        };

        fetchWeather();
        const weatherInterval = setInterval(fetchWeather, 15 * 60 * 1000);
        return () => clearInterval(weatherInterval);
    }, [weatherLat, weatherLon, weatherTimezone, getHourCount, selectedDate]);

    const formattedSelectedDate = selectedDate ? selectedDate.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
    }) : '';

    const formattedBaliTime = (baliNow || new Date()).toLocaleTimeString('en-GB', {
        timeZone: weatherTimezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    useEffect(() => {
        window.localStorage.setItem('vmb_live_conditions_variant', liveConditionsVariant);
    }, [liveConditionsVariant]);

    useEffect(() => {
        window.localStorage.setItem('vmb_temperature_unit', temperatureUnit);
    }, [temperatureUnit]);

    const formatTemperature = useCallback((valueInCelsius) => {
        const numericValue = Number(valueInCelsius);
        if (!Number.isFinite(numericValue)) return `0°${temperatureUnit}`;
        const displayValue = temperatureUnit === 'F'
            ? (numericValue * 9) / 5 + 32
            : numericValue;
        return `${Math.round(displayValue)}°${temperatureUnit}`;
    }, [temperatureUnit]);

    useEffect(() => {
        if (redirectReason !== 'missing_guest_params') return;
        if (!selectedVillaId || !lastName || !arrivalDate) return;

        const params = new URLSearchParams(window.location.search);
        params.delete('reason');
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
        window.history.replaceState({}, '', nextUrl);
    }, [redirectReason, selectedVillaId, lastName, arrivalDate]);

    const searchString = buildSearch(selectedVillaId, lastName, arrivalDate);

    const toggleWelcomeInstructions = () => {
        setIsWelcomeInstructionsVisible(v => !v);
    };
    const toggleMenuInfo = () => setIsMenuInfoVisible(v => !v);
    const toggleOrderConfirmationInfo = () => setIsOrderConfirmationInfoVisible(v => !v);

    useEffect(() => {
        if (!isGuestAuthorized || !selectedVillaId || !lastName || !arrivalDate) {
            setGuestData(null);
            return;
        }
        setLoadingGuestData(true);
        setGuestDataError(null);
        const params = new URLSearchParams({
            villa_id: selectedVillaId,
            last_name: lastName,
            checkin_date: arrivalDate
        });
        apiFetch(`${baseUrl}/guest/data?${params.toString()}`)
            .then(async response => {
                if (!response.ok) throw new Error('Error loading guest data.');
                return response.json();
            })
            .then(data => {
                const villaName = villas.find(v => v.id === selectedVillaId)?.name || '';
                setGuestData({ ...data, villaName });
            })
            .catch(err => setGuestDataError(err.message))
            .finally(() => setLoadingGuestData(false));
    }, [isGuestAuthorized, selectedVillaId, lastName, arrivalDate, baseUrl, villas]);

    useEffect(() => {
        if (!selectedDate || !selectedVillaId) {
            setTimeSlots([]);
            return;
        }
        const controller = new AbortController();
        setLoadingSlots(true);
        setError(null);
        const dateFormatted = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        const params = new URLSearchParams({
            date: dateFormatted,
            villa_id: selectedVillaId,
            last_name: lastName,
            checkin_date: arrivalDate
        });
        const timer = setTimeout(() => {
        apiFetch(`${baseUrl}/slots?${params.toString()}`, { signal: controller.signal })
            .then(async response => {
                if (!response.ok) {
                    let errorMessage = 'Error retrieving time slots.';
                    try {
                        const errorData = await response.json();
                        if (errorData.detail) errorMessage = errorData.detail;
                    } catch { }
                    throw new Error(errorMessage);
                }
                return response.json();
            })
            .then(data => {
                if (!data || data.length === 0) {
                    setError('Unfortunately your entered villa, last name and checkin date do not match with any entry in our guests database. Please check your entered parameter and try again.');
                    setTimeSlots([]);
                    return;
                }
                const validatedData = data.map(slot => ({ ...slot, is_available: typeof slot.is_available === 'boolean' ? slot.is_available : false }));
                setTimeSlots(validatedData);
            })
            .catch(err => {
                if (err.name === 'AbortError') return;
                setError(err.message);
                setTimeSlots([]);
            })
            .finally(() => setLoadingSlots(false));
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [selectedDate, selectedVillaId, baseUrl, lastName, arrivalDate]);

    useEffect(() => {
        setLoadingMenus(true);
        setMenuError(null);
        apiFetch(`${baseUrl}/menus`)
            .then(response => {
                if (!response.ok) throw new Error('Error retrieving menus.');
                return response.json();
            })
            .then(data => setMenus(normalizeMenus(data)))
            .catch(() => setMenuError('Menus could not be loaded. Please try again.'))
            .finally(() => setLoadingMenus(false));
    }, [baseUrl]);

    useEffect(() => {
        setLoadingVillas(true);
        setVillaError(null);
        apiFetch(`${baseUrl}/villas`)
            .then(response => {
                if (!response.ok) throw new Error('Error retrieving villas.');
                return response.json();
            })
            .then(data => setVillas(normalizeVillas(data)))
            .catch(() => setVillaError('Villas could not be loaded.'))
            .finally(() => setLoadingVillas(false));
    }, [baseUrl]);

    useEffect(() => {
        const orderItems = Object.keys(quantities)
            .filter(id => quantities[id] > 0)
            .map(id => ({
                id,
                quantity: quantities[id]
            }));
        if (orderItems.length === 0) {
            setOrderSummary(null);
            setSummaryError(null);
            return;
        }
        const controller = new AbortController();
        const fetchSummary = async () => {
            setLoadingSummary(true);
            setSummaryError(null);
            const dateFormatted = selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : null;
            try {
                const response = await apiFetch(`${baseUrl}/orders/create_summary`, {
                    method: 'POST',
                    signal: controller.signal,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        items: orderItems,
                        appointment_date: dateFormatted,
                        appointment_time: selectedTime
                    }),
                });
                if (!response.ok) {
                    setSummaryError('Failed to fetch order summary.');
                    setOrderSummary(null);
                    return;
                }
                const summaryData = await response.json();
                setOrderSummary(summaryData);
            } catch (error) {
                if (error.name === 'AbortError') return;
                setSummaryError(error.message);
                setOrderSummary(null);
            } finally {
                setLoadingSummary(false);
            }
        };
        const timer = setTimeout(() => {
            fetchSummary();
        }, 250);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [quantities, selectedDate, selectedTime, baseUrl]);

    const handleGuestAuthorization = useCallback(async () => {
        setAutoAuthSuppressed(false);
        setIsAuthorizing(true);
        setAuthError(null);
        if (!selectedVillaId || !lastName || !arrivalDate) {
            setAuthError("Please select a villa, enter a last name and a check-in date.");
            setIsAuthorizing(false);
            return;
        }
        try {
            const response = await apiFetch(`${baseUrl}/guest/authorize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    villa_id: selectedVillaId,
                    last_name: lastName,
                    checkin_date: arrivalDate
                })
            });

            if (response.ok) {
                const responseText = await response.text();
                let authorizationResult = null;

                if (responseText) {
                    try {
                        authorizationResult = JSON.parse(responseText);
                    } catch {
                        authorizationResult = responseText;
                    }
                }

                const isExplicitFailure =
                    authorizationResult === false ||
                    authorizationResult?.authorized === false ||
                    authorizationResult?.is_authorized === false ||
                    authorizationResult?.success === false;

                if (isExplicitFailure) {
                    setIsGuestAuthorized(false);
                    setGuestAuthToken('');
                    setAuthError("Guest information could not be confirmed. Please check your details.");
                    return;
                }

                const issuedToken = response.headers.get('X-Guest-Auth') || '';
                setIsGuestAuthorized(true);
                setGuestAuthToken(issuedToken);
                setAuthError(null);
            } else {
                setIsGuestAuthorized(false);
                setGuestAuthToken('');
                setAuthError("Guest information could not be confirmed. Please check your details.");
            }
        } catch {
            setIsGuestAuthorized(false);
            setGuestAuthToken('');
            setAuthError("Guest information could not be confirmed. Please check your details.");
        } finally {
            setIsAuthorizing(false);
        }
    }, [arrivalDate, baseUrl, lastName, selectedVillaId]);

    useEffect(() => {
        if (autoAuthSuppressed) return;
        if (!selectedVillaId || !lastName || !arrivalDate || isGuestAuthorized) return;
        const timer = setTimeout(() => {
            handleGuestAuthorization();
        }, 1000);
        return () => clearTimeout(timer);
    }, [arrivalDate, autoAuthSuppressed, handleGuestAuthorization, isGuestAuthorized, lastName, selectedVillaId]);

    const normalizeDate = (value) => {
        const date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const getMinDate = () => guestData ? new Date(guestData.arrival_date) : null;
    const getMaxDate = () => guestData ? new Date(guestData.departure_date) : null;
    const getCurrentDate = () => guestData ? new Date(guestData.current_date) : null;

    const tileDisabled = ({ date }) => {
        if (!guestData) return true;
        const arrival = normalizeDate(getMinDate());
        const departure = normalizeDate(getMaxDate());
        const current = normalizeDate(getCurrentDate());
        const candidate = normalizeDate(date);
        return candidate < arrival || candidate > departure || candidate < current;
    };

    const tileClassName = ({ date, view }) => {
        if (view === 'month' && guestData) {
            const current = normalizeDate(getCurrentDate());
            const candidate = normalizeDate(date);
            if (candidate.getTime() === current.getTime()) return 'react-calendar__tile--now';
        }
        return null;
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setSelectedTime(null);
        setOpenCategory(null);
        setError(null);
        setIsOrderSubmitted(false);
    };

    const getAvailableCategoryKeys = useCallback(() => {
        return CATEGORIES
            .map(category => category.key)
            .filter(categoryKey => menus.some(menu => menu.category === categoryKey));
    }, [menus]);

    const getMenuNavigationOffset = useCallback(() => {
        const fallbackNavigationHeight = 92;
        const navigationHeight = menuCategoryNavigationRef.current?.getBoundingClientRect().height || fallbackNavigationHeight;
        const safetyOffset = 12;
        return navigationHeight + safetyOffset;
    }, []);

    const scrollToMenuCategory = useCallback((categoryKey) => {
        const categoryElement = document.getElementById(`menu-category-${categoryKey}`);
        if (!categoryElement) return;

        const topOffset = getMenuNavigationOffset();
        const targetTop = categoryElement.getBoundingClientRect().top + window.scrollY - topOffset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
        });
    }, [getMenuNavigationOffset]);

    const scrollToMenuHeading = useCallback(() => {
        const target = menuCategoryContainerRef.current || menuHeaderContainerRef.current || menuHeadingRef.current || menuSelectionRef.current;
        if (!target) return;

        const topOffset = getMenuNavigationOffset();
        const targetTop = target.getBoundingClientRect().top + window.scrollY - topOffset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
        });
    }, [getMenuNavigationOffset]);

     const scrollToEditOrderTarget = useCallback(() => {
         const availableCategoryKeys = getAvailableCategoryKeys();
         if (availableCategoryKeys.length > 0) {
             // Scroll to the first available category
             const firstCategoryElement = document.getElementById(`menu-category-${availableCategoryKeys[0]}`);
             if (firstCategoryElement) {
                 const topOffset = getMenuNavigationOffset();
                 const targetTop = firstCategoryElement.getBoundingClientRect().top + window.scrollY - topOffset;
                 window.scrollTo({
                     top: Math.max(targetTop, 0),
                     behavior: 'smooth',
                 });
                 return true;
             }
         }

         // Fallback if no categories are available
         const target = menuCategoryNavigationRef.current || menuCategoryContainerRef.current || menuInfoRef.current;
         if (!target) return false;

         target.scrollIntoView({ behavior: 'smooth', block: 'start' });
         return true;
     }, [getAvailableCategoryKeys, getMenuNavigationOffset]);

     const scrollToFirstCategory = useCallback(() => {
         const availableCategoryKeys = getAvailableCategoryKeys();
         if (availableCategoryKeys.length > 0) {
             const firstCategoryElement = document.getElementById(`menu-category-${availableCategoryKeys[0]}`);
             if (firstCategoryElement) {
                 const topOffset = getMenuNavigationOffset();
                 const targetTop = firstCategoryElement.getBoundingClientRect().top + window.scrollY - topOffset;
                 window.scrollTo({
                     top: Math.max(targetTop, 0),
                     behavior: 'smooth',
                 });
             }
         }
     }, [getAvailableCategoryKeys, getMenuNavigationOffset]);

    const getOrderedSections = useCallback(() => {
        return [
            welcomeSectionRef.current,
            calendarSectionRef.current,
            menuCategoryContainerRef.current || menuSelectionRef.current,
            isGuestAuthorized && guestData ? orderSummarySectionRef.current : null,
        ].filter(Boolean);
    }, [guestData, isGuestAuthorized]);

    const getCurrentSectionIndex = useCallback((sections) => {
        const probeY = window.scrollY + navbarHeight + Math.max(window.innerHeight * 0.35, 160);
        let currentIndex = 0;

        sections.forEach((section, index) => {
            const top = section.getBoundingClientRect().top + window.scrollY;
            if (top <= probeY) {
                currentIndex = index;
            }
        });

        return currentIndex;
    }, [navbarHeight]);

    const scrollToSection = useCallback((section) => {
        if (!section) return;

        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

     useEffect(() => {
         if (isMobileCalendarLayout || !isGuestAuthorized) return;

         const handleWheel = (event) => {
             if (event.defaultPrevented) return;
             if (!(event.target instanceof Element)) return;
             if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;

             // Allow normal scrolling when a menu category is open
             if (openCategory !== null) {
                 return;
             }

             const sections = getOrderedSections();
             if (sections.length < 2) return;

             const direction = Math.sign(event.deltaY);
             if (direction === 0) return;

             if (wheelScrollLockRef.current) {
                 event.preventDefault();
                 return;
             }

             const currentIndex = getCurrentSectionIndex(sections);
             const targetIndex = direction > 0
                 ? Math.min(currentIndex + 1, sections.length - 1)
                 : Math.max(currentIndex - 1, 0);

             if (targetIndex === currentIndex) return;

             event.preventDefault();
             wheelScrollLockRef.current = true;
             scrollToSection(sections[targetIndex]);

             window.setTimeout(() => {
                 wheelScrollLockRef.current = false;
             }, 700);
         };

         window.addEventListener('wheel', handleWheel, { passive: false });
         return () => window.removeEventListener('wheel', handleWheel);
     }, [getCurrentSectionIndex, getOrderedSections, isGuestAuthorized, isMobileCalendarLayout, scrollToSection, openCategory]);

    const handleSlotSelection = (slotTime) => {
        const availableCategoryKeys = getAvailableCategoryKeys();
        const firstCategoryKey = availableCategoryKeys[0] || null;

        setSelectedTime(slotTime);
        setOpenCategory(firstCategoryKey);
        setError(null);
        setIsOrderSubmitted(false);

        setTimeout(() => {
            scrollToMenuHeading();
        }, 80);
    };

    const handleNextCategory = () => {
        const availableCategoryKeys = getAvailableCategoryKeys();
        if (availableCategoryKeys.length === 0) return;

        const currentIndex = availableCategoryKeys.indexOf(openCategory);
        if (currentIndex < 0 || currentIndex >= availableCategoryKeys.length - 1) return;

        const nextCategoryKey = availableCategoryKeys[currentIndex + 1];
        setOpenCategory(nextCategoryKey);

        setTimeout(() => {
            scrollToMenuCategory(nextCategoryKey);
        }, 80);
    };

    const handlePreviousCategory = () => {
        const availableCategoryKeys = getAvailableCategoryKeys();
        if (availableCategoryKeys.length === 0) return;

        const currentIndex = availableCategoryKeys.indexOf(openCategory);
        if (currentIndex <= 0) return;

        const previousCategoryKey = availableCategoryKeys[currentIndex - 1];
        setOpenCategory(previousCategoryKey);

        setTimeout(() => {
            scrollToMenuCategory(previousCategoryKey);
        }, 80);
    };

    const handleFinishMenuSelection = () => {
        setOpenCategory(null);
        setIsOrderConfirmationInfoVisible(true);

        setTimeout(() => {
            if (confirmationInfoRef.current) {
                confirmationInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 80);
    };

    useEffect(() => {
        if (isReloadScrollResetRef.current) return;
        if (!selectedDate) return;
        const timer = setTimeout(() => {
            if (timeSelectionRef.current) {
                timeSelectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 120);
        return () => clearTimeout(timer);
    }, [selectedDate, loadingSlots, timeSlots.length]);

    useEffect(() => {
        if (!selectedTime || loadingMenus) return;

        const availableCategoryKeys = getAvailableCategoryKeys();
        if (availableCategoryKeys.length === 0) return;

        if (!openCategory || !availableCategoryKeys.includes(openCategory)) {
            setOpenCategory(availableCategoryKeys[0]);
        }
    }, [selectedTime, loadingMenus, openCategory, getAvailableCategoryKeys]);

    useEffect(() => {
        if (isReloadScrollResetRef.current) return;
        // One-time check when menu targets become available
        const shouldScroll = window.sessionStorage.getItem('vmb_scroll_to_menu_info') === '1';
        if (shouldScroll && scrollToEditOrderTarget()) {
            window.sessionStorage.removeItem('vmb_scroll_to_menu_info');
        }
    }, [
        menuCategoryNavigationRef?.current ? 'has-nav-ref' : 'no-nav-ref',
        menuCategoryContainerRef?.current ? 'has-category-ref' : 'no-category-ref',
        menuInfoRef?.current ? 'has-info-ref' : 'no-info-ref',
        scrollToEditOrderTarget,
    ]);

     useEffect(() => {
         if (isReloadScrollResetRef.current) return;
         // Check for scroll-to-menu-info flag set by handleEditOrder
         const shouldScroll = window.sessionStorage.getItem('vmb_scroll_to_menu_info') === '1';

         if (shouldScroll && !loadingMenus) {
             // Wait for DOM to update after navigation
             const timer = setTimeout(() => {
                 if (scrollToEditOrderTarget()) {
                     window.sessionStorage.removeItem('vmb_scroll_to_menu_info');
                 }
             }, 150);

             return () => clearTimeout(timer);
         }
     }, [selectedTime, isGuestAuthorized, scrollToEditOrderTarget, loadingMenus]);

    const handleQuantityChange = async (menuId, value) => {
        const quantity = Math.max(0, parseInt(value, 10) || 0);
        const newQuantities = { ...quantities, [menuId]: quantity };
        setQuantities(newQuantities);
        const orderItems = Object.keys(newQuantities)
            .filter(id => newQuantities[id] > 0)
            .map(id => ({ id, quantity: newQuantities[id] }));
        try {
            const response = await apiFetch(`${baseUrl}/menus/update_prices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: orderItems }),
            });
            if (!response.ok) {
                setMenuError('Could not update prices. Please try again.');
                return;
            }
            const updatedMenus = normalizeMenus(await response.json());
            setMenus(updatedMenus);
        } catch (error) {
            console.error('Error updating prices:', error);
            setMenuError('Could not update prices. Please try again.');
        }
    };

    async function handleOrderSubmit() {
        setIsSubmitting(true);
        setSubmissionError(null);
        if (!guestData || !guestData.guest_id) { setSubmissionError('Guest data is not loaded or invalid.'); setIsSubmitting(false); return; }
        if (!selectedVillaId) { setSubmissionError('Please select a villa'); setIsSubmitting(false); return; }
        if (!arrivalDate) { setSubmissionError('Please enter your check-in date'); setIsSubmitting(false); return; }
        if (!selectedDate) { setSubmissionError('Please choose a date for your breakfast'); setIsSubmitting(false); return; }
        if (!selectedTime) { setSubmissionError('Please choose a time for your breakfast'); setIsSubmitting(false); return; }
        if (!guestAuthToken) { setSubmissionError('Your guest authorization has expired. Please confirm your guest information again.'); setIsSubmitting(false); return; }

        const orderItems = Object.keys(quantities)
            .filter(id => quantities[id] > 0)
            .map(id => {
                const menu = menus.find(m => m.id === id);
                if (!menu) return null;
                return {
                    id: menu.id,
                    name: menu.name,
                    quantity: quantities[id],
                    price: menu.price,
                    category: menu.category,
                    discount: menu.discount ?? null
                };
            })
            .filter(Boolean);

        const dateFormatted = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        const orderPayload = {
            name: "Guest",
            appointment_date: dateFormatted,
            appointment_time: selectedTime,
            order_type: "online",
            details: orderItems,
            notes,
            guest_id: guestData.guest_id,
            villa_id: selectedVillaId,
            arrival_date: arrivalDate,
            confirmation_e_mail: email,
        };
        try {
            const response = await apiFetch(`${baseUrl}/order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Guest-Auth': guestAuthToken,
                },
                body: JSON.stringify(orderPayload),
            });
            if (!response.ok) {
                let errorMessage = 'Order could not be sent. Please try again.';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail ? JSON.stringify(errorData.detail) : errorMessage;
                } catch (e) { /* Ignore if response is not JSON */ }
                setSubmissionError(errorMessage);
                setIsOrderSubmitted(false);
                return;
            }
            await response.json();
            setIsOrderSubmitted(true);

            setLoadingFetchedOrder(true);
            setFetchOrderError(null);
            const selectedVilla = villas.find(v => v.id === selectedVillaId);
            const villaName = selectedVilla ? selectedVilla.name : '';
            const queryParams = new URLSearchParams({
                villa_name: villaName,
                last_name: lastName,
                checkin_date: arrivalDate
            });
            const getResponse = await apiFetch(`${baseUrl}/orders_by_guest?${queryParams}`);
            if (!getResponse.ok) {
                setSubmissionError('Order could not be loaded after submission.');
                setIsOrderSubmitted(false);
                return;
            }
            const orders = await getResponse.json();
            const dateStr = dateFormatted;
            const targetMinutes = toMinutes(selectedTime);

            let foundOrders = orders.filter(
                o => o.appointment_date === dateStr && toMinutes(o.appointment_time) === targetMinutes
            );

            if (foundOrders.length === 0) {
                foundOrders = orders.filter(o => o.appointment_date === dateStr);
            }

            setFetchedOrder({
                orders: foundOrders,
                villa: villaName,
                lastName: lastName,
                date: dateFormatted,
                time: selectedTime,
                notes: notes,
                email: email
            });

        } catch (err) {
            setSubmissionError(err.message);
            setIsOrderSubmitted(false);
        } finally {
            setIsSubmitting(false);
            setLoadingFetchedOrder(false);
        }
    }

    const handleChangeGuestDetails = () => {
        setAutoAuthSuppressed(true);
        setIsGuestAuthorized(false);
        setGuestAuthToken('');
        setAuthError(null);
        setGuestData(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setOpenCategory(null);
    };


    const instructionsBlock = (
        <div className="instructions-section">
            <div className="instructions-title">Place a new order</div>
            <div className="instructions-toggle-container" onClick={toggleWelcomeInstructions}>
                <button className="instructions-toggle-btn">?</button>
                <span>Click for order instruction</span>
            </div>
            {isWelcomeInstructionsVisible && (
                <div className="instructions-content" onClick={toggleWelcomeInstructions}>
                    <p className="welcome-subtitle">
                        We are delighted to offer you a convenient way to order your breakfast online during your stay with us.
                    </p>
                    <p className="welcome-steps-title">Here are the simple steps to place your breakfast order:</p>
                    <ol className="welcome-steps">
                        <li>Select your villa, enter your last name and check-in date, then confirm your guest information.</li>
                        <li>Choose your preferred breakfast date and time.</li>
                        <li>Select your favorite menu items and specify the quantity.</li>
                        <li>Add any special requests or notes, and optionally enter your email for an order confirmation.</li>
                    </ol>
                    <p className="welcome-info">
                        Once you have completed your selection, simply place your order.
                        If you wish to review your previous orders, you can do so at any time.
                        We hope you enjoy your stay and your breakfast experience!
                    </p>
                </div>
            )}
        </div>
    );

    const menuInfosBlock = (
        <div className="instructions-section">
            <div className="instructions-title">Menu Information</div>
            <div className="instructions-toggle-container mt-10" onClick={toggleMenuInfo}>
                <button className="instructions-toggle-btn">?</button>
                <span>Click for menu information</span>
            </div>
            {isMenuInfoVisible && (
                <div className="instructions-content" onClick={toggleMenuInfo}>
                    <p className="menu-info">
                        In the following section, you can choose your breakfast dishes.<br />
                        Please note that when choosing a breakfast package,
                        you must always select a breakfast dish, a drink and a fruit platter.<br />
                        In addition to the breakfast packages, you can order additional dishes,
                        drinks or fruit platters.<br />
                        The price for these will be displayed separately.
                        A summary of the total price will be displayed below the menu selection.
                    </p>
                </div>
            )}
        </div>
    );

    const desktopWeatherSpacerBlock = <div className="desktop-weather-spacer" aria-hidden="true" />;

    const orderConfirmationInfoBlock = (
        <div className="order-confirmation-container">
            <div className="instructions-title">Confirmation information</div>
            <div className="instructions-toggle-container" onClick={toggleOrderConfirmationInfo}>
                <button className="instructions-toggle-btn">?</button>
                <span>Click for order confirmation information</span>
            </div>
            {isOrderConfirmationInfoVisible && (
                <div className="instructions-content" onClick={toggleOrderConfirmationInfo}>
                    <p className="order-confirmation-info">
                        In case you want to receive an order confirmation, please enter your e-mail address here.<br />
                        We won't use it for any other purpose.
                    </p>
                </div>
            )}
        </div>
    );

    if (isOrderSubmitted) {
        if (loadingFetchedOrder) {
            return (
                <div
                    className="order-confirmation-page"
                    style={{ backgroundImage: `url("${orderConfirmationBackgroundImageUrl}")` }}
                >
                    <div className="order-confirmation-content">
                        <h1>Loading your order...</h1>
                    </div>
                </div>
            );
        }
        if (fetchOrderError || !fetchedOrder) {
            return (
                <div
                    className="order-confirmation-page"
                    style={{ backgroundImage: `url("${orderConfirmationBackgroundImageUrl}")` }}
                >
                    <div className="order-confirmation-content">
                        <h1>Error</h1>
                        <p>{fetchOrderError || 'The order could not be loaded.'}</p>
                        <button
                            onClick={() => (window.location.href = `/${window.location.search}`)}
                            className="selection-btn"
                        >
                            New order
                        </button>
                    </div>
                </div>
            );
        }

        const allOrderItems = fetchedOrder.orders.flatMap(order => order.details);

        return (
            <div
                className="order-confirmation-page"
                style={{ backgroundImage: `url("${orderConfirmationBackgroundImageUrl}")` }}
            >
                <div className="order-confirmation-content">
                    <h1>Thank you for your order!</h1>
                    <p>Your order has been successfully placed.</p>
                    {fetchedOrder.email && <p>A confirmation has been sent to: <strong>{fetchedOrder.email}</strong></p>}
                    <div className="order-summary">
                        <h3>Order Summary</h3>
                        <div className="order-summary-details">
                            <p><strong>Villa:</strong> {fetchedOrder.villa}</p>
                            <p><strong>Last Name:</strong> {fetchedOrder.lastName}</p>
                            <p><strong>Date:</strong> {new Date(fetchedOrder.date).toLocaleDateString('en-GB')}</p>
                            <p><strong>Time:</strong> {fetchedOrder.time}</p>
                        </div>
                        {allOrderItems.length === 0 ? (
                            <p>No items could be matched to this time yet. Please refresh later if this seems incorrect.</p>
                        ) : (
                            <table className="orders-table">
                                <thead>
                                <tr>
                                    <th>Quantity</th>
                                    <th>Item</th>
                                    <th>Price</th>
                                </tr>
                                </thead>
                                <tbody>
                                {allOrderItems.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.quantity}</td>
                                        <td>{item.name}</td>
                                        <td>
                                            {item.price === 0 && item.discount > 0
                                                ? 'included'
                                                : item.price != null
                                                    ? item.price.toLocaleString('en-GB') + ' IDR'
                                                    : ''}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot>
                                <tr>
                                    <td colSpan="2" className="total-price-label">Total Price:</td>
                                    <td className="total-price-value">
                                        {orderSummary.total_price != null
                                            ? `${orderSummary.total_price.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IDR`
                                            : '0 IDR'}
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                        )}
                        {fetchedOrder.notes && (
                            <div className="order-notes">
                                <p><strong>Notes:</strong> {fetchedOrder.notes}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => (window.location.href = `/${window.location.search}`)}
                        className="selection-btn"
                    >
                        NEW ORDER
                    </button>
                    <div className="terms-and-conditions">
                        <p>© 2025 Villa Mia﻿ | All Rights Reserved | Powered by Dsto</p>
                    </div>
                </div>
            </div>
        );
    }

    const isCheckOrdersDisabled = !selectedVillaId || !lastName || !arrivalDate;
    const availableCategories = CATEGORIES.filter(category => menus.some(menu => menu.category === category.key));
    const availableCategoryKeys = availableCategories.map(category => category.key);
    const currentCategoryIndex = availableCategoryKeys.indexOf(openCategory);
    const hasPreviousCategory = currentCategoryIndex > 0;
    const hasNextCategory = currentCategoryIndex >= 0 && currentCategoryIndex < availableCategoryKeys.length - 1;
    const shouldShowMenuSelectionSection = true;

    const liveConditionsCard = selectedDate ? (
        <div className={`live-conditions-card ${liveConditionsVariant}`} aria-label="Location time and weather">
            <div className="live-conditions-main">
                <div className="live-conditions-header">
                    <div className="live-conditions-location">
                        <div className="live-conditions-title-row">
                            <strong>{weatherPlace}</strong>
                            <span className="live-conditions-local-time">Local time now: {formattedBaliTime}</span>
                        </div>
                        <span className="live-conditions-subtitle">
                            Forecast for selected date
                        </span>
                        <div className="live-conditions-row">
                            <span>{formattedSelectedDate}</span>
                        </div>
                    </div>
                </div>

                {weatherLoading ? (
                    <p className="live-conditions-status">Loading weather...</p>
                ) : weatherError ? (
                    <p className="live-conditions-status">{weatherError}</p>
                ) : weatherNow ? (
                    <div className="live-conditions-now">
                        <span className="live-conditions-now-icon" aria-hidden="true">{weatherNow.icon}</span>
                        <span>First forecast</span>
                        <strong>{formatTemperature(weatherNow.temperature)}</strong>
                        <span>{weatherNow.description}</span>
                    </div>
                ) : null}
            </div>

            <div className="live-conditions-controls">
                <div className="live-conditions-variant-toggle" role="group" aria-label="Weather card style">
                    <button
                        type="button"
                        className={liveConditionsVariant === 'soft' ? 'active' : ''}
                        onClick={() => setLiveConditionsVariant('soft')}
                    >
                        Soft
                    </button>
                    <button
                        type="button"
                        className={liveConditionsVariant === 'compact' ? 'active' : ''}
                        onClick={() => setLiveConditionsVariant('compact')}
                    >
                        Compact
                    </button>
                </div>
                <div className="live-conditions-unit-toggle" role="group" aria-label="Temperature unit">
                    <button
                        type="button"
                        className={temperatureUnit === 'C' ? 'active' : ''}
                        onClick={() => setTemperatureUnit('C')}
                    >
                        °C
                    </button>
                    <button
                        type="button"
                        className={temperatureUnit === 'F' ? 'active' : ''}
                        onClick={() => setTemperatureUnit('F')}
                    >
                        °F
                    </button>
                </div>
            </div>

            {!weatherLoading && !weatherError && (
                <div className="live-conditions-forecast">
                    {nextFiveHoursWeather.map(item => (
                        <div key={item.time} className="live-conditions-forecast-item">
                            <div className="live-conditions-forecast-top">
                                <strong className="live-conditions-forecast-time">{String(item.time).slice(11, 16)}</strong>
                                <span className="live-conditions-forecast-icon" aria-hidden="true">{item.icon}</span>
                            </div>
                            <div className="live-conditions-forecast-bottom">
                                <span className="live-conditions-forecast-metric live-conditions-forecast-temp">
                                    <span aria-hidden="true">🌡</span>
                                    <span>{formatTemperature(item.temperature)}</span>
                                </span>
                                <span className="live-conditions-forecast-metric live-conditions-forecast-rain">
                                    <span aria-hidden="true">💧</span>
                                    <span>{item.precipitationProbability ?? 0}%</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ) : null;

     return (
         <Router>
             <Routes>
                 <Route path="/" element={
                     <div className="app-container">
                         <Navbar navbarOpen={navbarOpen} setNavbarOpen={setNavbarOpen} />
                          {showConfirmationModal && (
                              <ConfirmationModal
                                  orderSummary={orderSummary}
                                  selectedDate={selectedDate}
                                  selectedTime={selectedTime}
                                  submissionError={submissionError}
                                  isSubmitting={isSubmitting}
                                  handleOrderSubmit={handleOrderSubmit}
                                  onClose={() => setShowConfirmationModal(false)}
                                  searchString={searchString}
                                  scrollToFirstCategory={scrollToFirstCategory}
                              />
                          )}
                         <div className="main-content-container">
                            <section
                                aria-label="welcome-section"
                                className="welcome-section"
                                ref={welcomeSectionRef}
                                style={{ '--welcome-min-height': `calc(100dvh - ${navbarHeight}px)` }}
                            >
                                <div className="welcome-container">
                                    <div className="guest-greeting-container">
                                        <div className="guest-greeting-container-bg"></div>
                                        <div className="guest-greeting">
                                            {isGuestAuthorized && guestData && guestData.first_name
                                                ? <p>Dear {guestData.first_name},<br />Welcome to our Breakfast Portal!</p>
                                                : <p>Welcome to our<br />Villa Mia Breakfast Order Portal!</p>
                                            }
                                        </div>
                                    </div>
                                    {redirectReason === 'missing_guest_params' && (
                                        <p className="menu-selection-error">
                                            Your previous link was incomplete. Please re-enter your villa, last name and check-in date.
                                        </p>
                                    )}

                                    {isGuestAuthorized && guestData ? (
                                        <div className="guest-details-summary">
                                            <p>Last name: {lastName}<br />
                                            Villa: {guestData.villaName}<br />
                                            Check-in date: {new Date(arrivalDate).toLocaleDateString('en-GB')}</p>
                                            <div className="button-3-column">
                                                <button
                                                    onClick={handleChangeGuestDetails}
                                                    className="selection-btn"
                                                >
                                                    CHANGE GUEST DETAILS
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const section = document.querySelector('[aria-label="calendar-and-time-selection"]');
                                                        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }}
                                                    className="selection-btn start-ordering-btn"
                                                >
                                                    START ORDERING
                                                </button>
                                                <Link
                                                    to={`/existing-orders?${searchString}`}
                                                    className={`selection-link`}
                                                    onClick={(e) => isCheckOrdersDisabled && e.preventDefault()}
                                                    role="button"
                                                    aria-disabled={isCheckOrdersDisabled}
                                                >
                                                    VIEW / MODIFY EXISTING ORDERS
                                                </Link>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="order-details-container">
                                                <div className="guest-details-summary">
                                                    <div className="guest-details-summary-input">
                                                        <label>1. Select your villa:</label>
                                                        {loadingVillas ? <p>Loading villas...</p> : villaError ? <p className="menu-selection-error">{villaError}</p> :
                                                            <select
                                                                value={selectedVillaId}
                                                                onChange={e => setSelectedVillaId(e.target.value)}
                                                                className="menu-selection-input"
                                                                disabled={isGuestAuthorized}
                                                            >
                                                                <option value="">-- Choose Villa --</option>
                                                                {villas.map(villa => <option key={villa.id} value={villa.id}>{villa.name}</option>)}
                                                            </select>
                                                        }
                                                    </div>
                                                    <div className="guest-details-summary-input">
                                                        <label>2. Enter your last name:</label>
                                                        <input
                                                            type="text"
                                                            value={lastName}
                                                            onChange={e => setLastName(e.target.value)}
                                                            className="menu-selection-input"
                                                            disabled={isGuestAuthorized}
                                                        />
                                                    </div>
                                                    <div className="guest-details-summary-input">
                                                        <label>3. Enter your check-in date:</label>
                                                        <input
                                                            type="date"
                                                            value={arrivalDate}
                                                            onChange={e => setArrivalDate(e.target.value)}
                                                            className="menu-selection-input"
                                                            disabled={isGuestAuthorized}
                                                        />
                                                    </div>
                                                    <br />
                                                    <div className="button-column">
                                                        <button
                                                            onClick={handleGuestAuthorization}
                                                            disabled={isAuthorizing || isGuestAuthorized || !selectedVillaId || !lastName || !arrivalDate}
                                                            className={`selection-btn ${isAuthorizing || isGuestAuthorized || !selectedVillaId || !lastName || !arrivalDate ? 'menu-selection-btn-disabled' : ''}`}
                                                        >
                                                            {isAuthorizing ? 'CONFIRMING...' : 'CONFIRM GUEST INFORMATION'}
                                                        </button>
                                                    </div>
                                                </div>
                                                {authError && <p className="menu-selection-error">{authError}</p>}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </section>

                            <section
                                aria-label="calendar-and-time-selection"
                                className={isGuestAuthorized ? 'calendar-time-section' : ''}
                                style={isGuestAuthorized ? { '--calendar-min-height': `calc(100dvh - ${navbarHeight}px)` } : undefined}
                                ref={calendarSectionRef}
                            >
                                    {isGuestAuthorized && (
                                        <>
                                        <div className="instruction-container">
                                            {instructionsBlock}
                                        </div>
                                            <div className={`calendar-and-time-selection ${selectedDate ? 'has-selected-date' : ''}`}>
                                                <div className="calendar-container">
                                                    <div className="selection-column">
                                                        <h3>1. Choose a date for your breakfast:</h3>
                                                        {loadingGuestData && <p>Loading available dates...</p>}
                                                        {guestDataError && <p className="menu-selection-error">{guestDataError}</p>}
                                                        <div className="calendar-wrapper">
                                                            <Calendar
                                                                onChange={handleDateChange}
                                                                value={selectedDate}
                                                                tileDisabled={tileDisabled}
                                                                tileClassName={tileClassName}
                                                                locale="en-US"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {selectedDate && (
                                                    <div className={`timeslot-container ${isMobileCalendarLayout ? 'has-weather' : ''}`} ref={timeSelectionRef}>
                                                        <div className="selection-column">
                                                            <h3>2. Choose a time for your breakfast:</h3>
                                                            {loadingSlots ? <p>Loading time slots...</p> : error ? <p className="error-message">{error}</p> :
                                                                <div className="timeslot-grid">
                                                                    {timeSlots.map(slot => (
                                                                        <button
                                                                            key={slot.time}
                                                                            onClick={() => handleSlotSelection(slot.time)}
                                                                            disabled={!slot.is_available}
                                                                            className={`timeslot-btn ${selectedTime === slot.time ? 'selected' : ''}`}
                                                                        >
                                                                            {slot.time}am
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            }
                                                            {isMobileCalendarLayout && liveConditionsCard}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {!isMobileCalendarLayout && liveConditionsCard}
                                        </>
                                    )}
                            </section>
                            {shouldShowMenuSelectionSection && (
                            <section aria-label="menu-selection" ref={menuSelectionRef}>
                                <div className="menu-selection-container">
                                    <div className="instruction-container">
                                        <div ref={menuInfoRef}>
                                            {isMobileCalendarLayout
                                                ? menuInfosBlock
                                                : (isGuestAuthorized ? desktopWeatherSpacerBlock : null)}
                                        </div>
                                    </div>
                                    {selectedTime && availableCategoryKeys.length > 0 && (
                                        <div
                                            className="menu-category-navigation"
                                            ref={menuCategoryNavigationRef}
                                        >
                                            <button
                                                type="button"
                                                className={`selection-btn menu-step-btn ${!hasPreviousCategory ? 'menu-step-btn-disabled' : ''}`}
                                                onClick={handlePreviousCategory}
                                                disabled={!hasPreviousCategory}
                                            >
                                                PREVIOUS CATEGORY
                                            </button>
                                            <button
                                                type="button"
                                                className={`selection-btn menu-step-btn ${!hasNextCategory ? 'menu-step-btn-disabled' : ''}`}
                                                onClick={handleNextCategory}
                                                disabled={!hasNextCategory}
                                            >
                                                NEXT CATEGORY
                                            </button>
                                            <button
                                                type="button"
                                                className="selection-btn menu-step-btn"
                                                onClick={handleFinishMenuSelection}
                                            >
                                                FINISH ORDER & GO TO CONFIRMATION
                                            </button>
                                        </div>
                                    )}
                                    <div className="menu-category" ref={menuCategoryContainerRef}>
                                        <div ref={menuHeaderContainerRef}>
                                            {selectedTime && <h3 ref={menuHeadingRef}>3. Choose your favourite menu</h3>}
                                        </div>
                                        {loadingMenus ? (
                                            <p>Menus are loading...</p>
                                        ) : menuError ? (
                                            <p className="menu-selection-error">{menuError}</p>
                                        ) : (
                                            availableCategories.map(cat => {
                                                const categoryMenus = menus.filter(menu => menu.category === cat.key);
                                                return (
                                                    <div key={cat.key} id={`menu-category-${cat.key}`} className="menu-category">
                                                        <button
                                                            className="menu-category-btn"
                                                            onClick={() => setOpenCategory(openCategory === cat.key ? null : cat.key)}
                                                        >
                                                            {cat.label}
                                                            <span className="menu-category-arrow">{openCategory === cat.key ? '▲' : '▼'}</span>
                                                        </button>
                                                        {openCategory === cat.key && (
                                                            <div className="menu-category-items">
                                                                {categoryMenus.map(menu => (
                                                                    <div key={menu.id} className="menu-item">
                                                                        <div className="menu-item-content">
                                                                            <div className="menu-item-image-wrapper">
                                                                                <MenuItemImage src={menu.image_path} alt={menu.name} className="menu-item-image" />
                                                                            </div>
                                                                            <div className="menu-item-info">
                                                                                <div className="menu-item-title">{menu.name}</div>
                                                                                <p className="menu-item-desc"><b></b><br />{menu.description}</p>
                                                                                {menu.ingredients && (
                                                                                    <p className="menu-item-ingredients"><b>Ingredients:</b><br />{menu.ingredients}</p>
                                                                                )}
                                                                                <p className="menu-item-price">
                                                                                    <b>Price per unit:</b><br />
                                                                                    {menu.price === 0
                                                                                        ? 'no extra charge'
                                                                                        : menu.price != null
                                                                                            ? `${menu.price} IDR`
                                                                                            : ''}
                                                                                </p>
                                                                            </div>
                                                                            <div className="menu-item-quantity-container">
                                                                                <table className="menu-item-quantity-table">
                                                                                    <tbody>
                                                                                    <tr>
                                                                                        <td></td>
                                                                                    </tr>
                                                                                    <tr>
                                                                                        <td>
                                                                                            <div className="menu-item-quantity">
                                                                                                <QuantityControl
                                                                                                    value={quantities[menu.id] || 0}
                                                                                                    onChange={val => handleQuantityChange(menu.id, val)}
                                                                                                />
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                    <tr>
                                                                                        <td></td>
                                                                                    </tr>
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                        {isGuestAuthorized && guestData && (
                                        <div className="menu-selection-comments">
                                            <h3>Comments on the order</h3>
                                            <textarea
                                                value={notes}
                                                onChange={e => setNotes(e.target.value)}
                                                placeholder="Here you can enter any special requests or notes..."
                                                className="menu-selection-textarea"
                                            />
                                        </div>
                                        )}
                                    </div>
                                    {isGuestAuthorized && guestData && (
                                        <div className="order-confirmation-container" ref={confirmationInfoRef}>
                                            <div className="instruction-container">
                                                {orderConfirmationInfoBlock}
                                            </div>
                                            <div className="order-confirmation-container-input">
                                                <label htmlFor="email">E-Mail (optional, for order confirmation):</label>
                                                <br />
                                                <input
                                                    type="email"
                                                    id="email"
                                                    value={email}
                                                    onChange={e => setEmail(e.target.value)}
                                                    className="menu-selection-input"
                                                    placeholder="Enter your e-mail address for an order confirmation"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </section>
                            )}
                            {isGuestAuthorized && guestData && (
                            <section aria-label="order-summary" ref={orderSummarySectionRef}>
                                <div className="order-summary-container">
                                    {loadingSummary && <p>Loading summary...</p>}
                                    {summaryError && <p className="menu-selection-error">{summaryError}</p>}
                                    {orderSummary && (
                                        <div className="order-summary-actions-row">
                                            <div className="order-summary-section">
                                                <h3>Order Summary</h3>
                                                {selectedDate && selectedTime && <p>Your selection for the date {selectedDate.toLocaleDateString('en-GB')} at {selectedTime}am</p>}
                                                <table className="orders-table">
                                                <thead>
                                                <tr>
                                                    <th>Quantity</th>
                                                    <th>Item</th>
                                                    <th>Price</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {orderSummary.order_items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.quantity}</td>
                                                        <td>{item.name}</td>
                                                        <td>
                                                            {item.price === 0 && item.discount > 0
                                                                ? 'included'
                                                                : item.price != null
                                                                    ? item.price.toLocaleString('en-GB') + ' IDR'
                                                                    : ''}
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                                <tfoot>
                                                <tr>
                                                    <td colSpan="2" className="total-price-label">Total Price:</td>
                                                    <td className="total-price-value">
                                                        {orderSummary.total_price != null
                                                            ? `${orderSummary.total_price.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IDR`
                                                            : '0 IDR'}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                                </tfoot>
                                                </table>
                                            </div>
                                            <div className="order-accept-section">
                                                <table className="accept-table">
                                                    <tbody>
                                                    <tr>
                                                        <td> </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <button
                                                                onClick={() => setShowConfirmationModal(true)}
                                                                className={`order-confirm-btn-submit`}
                                                            >
                                                                PLACE ORDER
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td></td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>
                            )}
                            <Footer feedbackSearch={searchString} />
                        </div>
                    </div>
                } />
                <Route path="/existing-orders" element={<ExistingOrdersPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/management/*" element={<ManagementPage />} />
             </Routes>
         </Router>
     );
}

export default App;
